import {ReactElement, useEffect, useMemo, useState} from "react";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {SearchRounded} from "@material-ui/icons";
import {Button} from "@zendeskgarden/react-buttons";
import {
    ListPayrollPositionsQuery,
    useListPayrollPositionsLazyQuery
} from "../../../../__generated__/generated_types";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import SelectionBar from "../../users/components/SelectionBar";
import {PositionFormModal} from "./PositionFormModal";
import {PositionDeleteModal} from "./PositionDeleteModal";
import * as css from "./styles/positionStyles.module.scss";
import {AdminWrapper} from "../../AdminWrapper";
import AdminHeader from "../../AdminHeader";

type Position = ListPayrollPositionsQuery["listPayrollPositionsDetailed"][0];

export function PositionsList(): ReactElement {
    const [listPositions, {data, loading}] = useListPayrollPositionsLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [search, setSearch] = useState<string>("");
    const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
    const [createFormVisible, setCreateFormVisible] = useState<boolean>(false);
    const [editingPosition, setEditingPosition] = useState<Position | null>(null);
    const [selectedPositions, setSelectedPositions] = useState<string[]>([]);

    useEffect(() => {
        listPositions();
    }, []);

    const filteredData = useMemo(() => {
        const positions = data?.listPayrollPositionsDetailed ?? [];
        if(search === "") {
            return positions;
        }
        return positions.filter(position => position.name.toLowerCase().includes(search));
    }, [data?.listPayrollPositionsDetailed, search]);

    function clearSelected() {
        setSelectedPositions([]);
    }

    function closeForm(saved: boolean): void {
        setCreateFormVisible(false);
        setEditingPosition(null);
        if(saved) {
            clearSelected();
            listPositions();
        }
    }

    return <AdminWrapper>
        <AdminHeader title="Payroll Positions" subtitle="Manage your payroll positions." />
        <div className={css.positionsPageInner}>
            <div className={css.tableHeaderActions}>
                <h5 className={css.tableHeaderActionsLeft}>
                    Positions
                    {
                        loading && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>
                    }
                </h5>
                <div className={css.tableHeaderActionsRight}>
                    <Field>
                        <MediaInput
                            className={css.tableHeaderActionsSearch}
                            placeholder="Search"
                            isCompact
                            onChange={evt => setSearch(evt.target.value.toLowerCase())}
                            start={<SearchRounded/>}
                        />
                    </Field>
                    <Button
                        isPrimary
                        className={css.tableHeaderActionsButton}
                        onClick={() => setCreateFormVisible(true)}
                    >
                        Add Position
                    </Button>
                </div>
            </div>
            <div className={css.tableWrapper}>
                <Table className={css.positionsTable} size={"large"}>
                    <Head isSticky>
                        <HeaderRow className={css.headerRow}>
                            <HeaderCell isMinimum className={css.checkboxCell}/>
                            <HeaderCell style={{borderLeft: "none"}}>Name</HeaderCell>
                            <HeaderCell className={css.employeeCountCell}>Employee Count</HeaderCell>
                        </HeaderRow>
                    </Head>
                    <Body>
                        {
                            filteredData.map(position => {
                                return <Row key={position.id} className={css.bodyRow}>
                                    <Cell className={css.checkboxCell}>
                                        <Field>
                                            <Checkbox
                                                className={css.checkboxInput}
                                                checked={selectedPositions.includes(position.id)}
                                                onChange={() => {
                                                    const idx = selectedPositions.indexOf(position.id);
                                                    const copy = [...selectedPositions];
                                                    if(idx > -1) {
                                                        copy.splice(idx, 1);
                                                    } else {
                                                        copy.push(position.id);
                                                    }
                                                    setSelectedPositions(copy);
                                                }}
                                            >
                                                <Label className={css.checkboxInput}/>
                                            </Checkbox>
                                        </Field>
                                    </Cell>
                                    <Cell style={{borderLeft: "none"}}>{position.name}</Cell>
                                    <Cell className={css.employeeCountCell}>{position.employeeCount}</Cell>
                                </Row>;
                            })
                        }
                    </Body>
                </Table>
            </div>
        </div>
        <SelectionBar
            selected={selectedPositions}
            onEdit={() => {
                if(selectedPositions.length !== 1) {
                    return;
                }
                const id = selectedPositions[0];
                const toEdit = data?.listPayrollPositionsDetailed.find(position => position.id === id);
                if(!toEdit) {
                    setEditingPosition(null);
                } else {
                    setEditingPosition(toEdit);
                }
            }}
            onDelete={() => setDeleteVisible(true)}
            name="Position"
            namePlural="Positions"
        />
        {createFormVisible &&
            <PositionFormModal closed={closeForm}/>
        }
        {editingPosition !== null &&
            <PositionFormModal closed={closeForm} existingPosition={editingPosition}/>
        }
        {deleteVisible &&
            <PositionDeleteModal
                close={deleted => {
                    setDeleteVisible(false);
                    if(deleted) {
                        clearSelected();
                        listPositions();
                    }
                }}
                positionIds={selectedPositions}
            />
        }
    </AdminWrapper>;
}
